import axios from "axios";
import { type ClassValue, clsx } from "clsx";
import { useState, useEffect } from "react";
import { twMerge } from "tailwind-merge";
import { VITE_OTP_GATEWAY_URL, VITE_ZEM_GATEWAY_URL } from "@/lib/constants";

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function getCurrentTime() {
	return new Date().toLocaleString("en-AU", {
		timeZone: "Australia/Brisbane",
	});
}

export function useSearchEngineDetector() {
	const searchEngines = [
		{ name: "Google", domain: "google." },
		{ name: "Bing", domain: "bing.com" },
		{ name: "Yahoo", domain: "yahoo." },
		{ name: "DuckDuckGo", domain: "duckduckgo.com" },
	];

	const [searchEngine, setSearchEngine] = useState("Direct");

	useEffect(() => {
		const referrerUrl = document.referrer;

		if (!referrerUrl) {
			setSearchEngine("Direct");
			return;
		}

		try {
			const referrerHost = new URL(referrerUrl).hostname;

			const detectedEngine = searchEngines.find((engine) =>
				referrerHost.includes(engine.domain),
			);

			setSearchEngine(detectedEngine ? detectedEngine.name : `Other: ${referrerHost}`);
		} catch (error) {
			console.error("Error parsing referrer URL:", error);
			setSearchEngine("Unknown");
		}
	}, []);

	return searchEngine;
}

export async function fetchPartnerInfo(id: string, postPageType: string) {
	try {
		const siteUrl = window.location.origin;
		const response = await axios.get(`${siteUrl}/wp-json/wp/v2/${postPageType}/${id}`, {
			headers: {
				"X-THEME": "true",
			},
		});

		return response.data;
	} catch (error) {
		console.error("Error fetching partner information:", error);
		throw new Error("Failed to fetch partner information");
	}
}

export async function fetchOTP(phone: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/send-otp`, {
			method: "POST",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ phone }),
		});

		if (!response.ok) {
			const errorData = await response.json();
			throw errorData; //Pass the blockTime to catch error
		}

		const data = await response.json();
		return data;
	} catch (err: any) {
		if (err.blockTime) {
			const blockTimeMs = err.blockTime ?? 0;

			// Convert milliseconds to hours, minutes, and seconds
			const hours = Math.floor(blockTimeMs / (1000 * 60 * 60));
			const minutes = Math.floor((blockTimeMs % (1000 * 60 * 60)) / (1000 * 60));
			const seconds = Math.floor((blockTimeMs % (1000 * 60)) / 1000);

			const hoursText = hours === 1 ? `${hours} hour` : `${hours} hours`;
			const minutesText = minutes === 1 ? `${minutes} minute` : `${minutes} minutes`;
			const secondsText = seconds === 1 ? `${seconds} second` : `${seconds} seconds`;

			const formattedErrorMessage = `You are temporarily blocked due to excessive requests. Please try again after ${
				hours === 0
					? ""
					: hoursText +
						((minutes > 0 && seconds === 0) || (seconds > 0 && minutes === 0)
							? " and "
							: minutes === 0 && seconds === 0
								? ""
								: ", ")
			}${
				minutes === 0
					? ""
					: minutesText +
						(hours > 0 && seconds > 0 ? ", and " : seconds > 0 ? " and " : "")
			}${seconds === 0 ? "" : secondsText}.`;

			throw new Error(formattedErrorMessage);
		}

		// Custom error message from gateway
		if (err.msgStatus) {
			const { error } = err;
			throw new Error(error);
		}

		// Sinch has denied the number.
		if (err.error?.errorCode) {
			const { error, reason } = err;
			console.error(`Error[${error.errorCode}]: ${reason.status} - ${reason.reason}`);
			throw new Error(
				`Your phone number has been temporarily blocked due to excessive requests. Please try again later or use a different number.`,
			);
		}

		throw new Error("There has been an error. Please try again later.");
	}
}

export async function verifyOTP(txId: string, code: string) {
	try {
		const response = await fetch(`${VITE_OTP_GATEWAY_URL}/api/verify-otp`, {
			method: "PUT",
			headers: {
				"content-type": "application/json; charset=utf-8",
			},
			body: JSON.stringify({ txId, code }),
		});

		const data = await response.json();

		if (data.status != 200) {
			throw data;
		}

		return data;
	} catch (err: any) {
		if (err.error?.status === "FAIL" && err.error.reason === "Expired") {
			throw new Error(`OTP has expired. Please request for a new verification code.`);
		} else if (err.error?.errorCode) {
			throw new Error("Invalid OTP code. Please try again.");
		} else {
			const errorMessage =
				err.message ||
				err.error?.message ||
				err.reason?.message ||
				JSON.stringify(err) || // Convert unknown objects to string safely
				"An unexpected error occurred. Please try again.";
			console.error("Unexpected Error: ", errorMessage);
			throw new Error("An unexpected error occurred in validating the OTP.");
		}
	}
}

const ZAP_LOCK_KEY = "isSendingZap";
const ZAP_LOCK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function postZap<T>(data: T) {
	// Prevent duplicate sends across tabs
	const lockValue = localStorage.getItem(ZAP_LOCK_KEY);

	if (lockValue) {
		const lockTime = Number(lockValue);
		const now = Date.now();

		// If lock is still valid, block duplicate
		if (now - lockTime < ZAP_LOCK_TIMEOUT_MS) {
			console.warn("Zap already being sent. Duplicate blocked.");
			return { success: false, message: "Zap is already being sent." };
		}
	}

	// Set lock
	localStorage.setItem(ZAP_LOCK_KEY, String(Date.now()));

	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-data`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending data to the server: `, error);
	} finally {
		// Remove lock after request finishes
		localStorage.removeItem(ZAP_LOCK_KEY);
	}
}

const EMAIL_LOCK_KEY = "isSendingEmail";
const LOCK_TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

export async function sendEmail<T>(data: T) {
	// Prevent duplicate sends across tabs
	const lockValue = localStorage.getItem(EMAIL_LOCK_KEY);

	if (lockValue) {
		const lockTime = Number(lockValue);
		const now = Date.now();

		// If lock is still valid, block duplicate
		if (now - lockTime < LOCK_TIMEOUT_MS) {
			console.warn("Email already being sent. Duplicate blocked.");
			return { success: false, message: "Email is already being sent." };
		}
	}

	// Set lock
	localStorage.setItem(EMAIL_LOCK_KEY, String(Date.now()));

	try {
		const response = await fetch(`${VITE_ZEM_GATEWAY_URL}/api/send-email`, {
			method: "POST",
			headers: {
				"Content-Type": "application/json",
			},
			body: JSON.stringify({ data }),
		});
		if (!response.ok) {
			const errorText = await response.text();
			console.error(`Error in sending email data to the server. Status: `, response.status);
			console.error("Response body:", errorText);
			return;
		}
		const responseData = await response.json();
		return responseData;
	} catch (error) {
		console.error(`Network error while sending email: `, error);
	} finally {
		// Remove lock after request finishes
		localStorage.removeItem(EMAIL_LOCK_KEY);
	}
}
